import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, LifeBuoy, MessageSquare, MessageCircle, Phone, SendHorizonal, ShieldCheck } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { supportService } from '../services/supportService';

interface SupportMessage {
  id: string;
  senderRole: string;
  senderName: string;
  message: string;
  createdAt: string;
}

export function DriverMobileSupportCenter() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [threadStatus, setThreadStatus] = useState<string>('open');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [supportPhone, setSupportPhone] = useState('');

  const user = useMemo(() => {
    const raw = sessionStorage.getItem('driverMobileUser');
    return raw ? JSON.parse(raw) : null;
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    loadThread();
  }, [navigate]);

  const loadThread = async () => {
    try {
      setLoading(true);
      const [threadResponse, contactResponse] = await Promise.all([
        supportService.getMyThread(),
        supportService.getMyContact(),
      ]);

      const payload: any = threadResponse.data || threadResponse;
      const contactPayload: any = contactResponse.data || contactResponse;
      setMessages(payload?.messages || []);
      setThreadStatus(payload?.thread?.status || 'open');
      setSupportPhone(contactPayload?.phone || '');
    } catch (error) {
      console.error('Support thread load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const normalizedSupportPhone = supportPhone.replace(/\D/g, '');
  const whatsappSupportMessage = encodeURIComponent('Merhaba, surucu uygulamasi destek hattina ulasiyorum.');

  const handleSend = async () => {
    if (!draft.trim()) return;
    try {
      setSending(true);
      await supportService.sendMyMessage(draft.trim());
      setDraft('');
      await loadThread();
    } catch (error) {
      console.error('Support message send failed:', error);
      alert('Mesaj gonderilirken bir hata olustu');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-slate-50 dark:bg-[#0b0b0b]">
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Yardim ve Destek</h1>
          <div className="w-9" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/10 dark:backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-200 dark:bg-white/15">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-200">Destek durumu</p>
              <p className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                {threadStatus === 'answered' ? 'Destek ekibi yanit verdi' : threadStatus === 'waiting-driver' ? 'Surucu geri donusu bekleniyor' : 'Mesajlariniz takip ediliyor'}
              </p>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-300">Operasyon, odeme, evrak ve teknik sorunlar icin buradan yazabilirsiniz.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div className="text-sm text-slate-600">
            Mesajlariniz dogrudan operasyon ekibine iletilir. Size verilen yanitlari bu ekran uzerinden takip edebilirsiniz.
          </div>
        </div>

        {supportPhone && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-sm text-slate-700 mb-3">Irtibat Hatti: <strong>{supportPhone}</strong></div>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`https://wa.me/${normalizedSupportPhone}?text=${whatsappSupportMessage}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-medium text-white hover:bg-green-700"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Mesaj At
              </a>
              <a
                href={`tel:${normalizedSupportPhone}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 py-2.5 text-sm font-medium text-white hover:bg-sky-700"
              >
                <Phone className="w-4 h-4" />
                Ara
              </a>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Mesajlasma</h2>
            <span className="text-xs text-slate-500">{messages.length} mesaj</span>
          </div>

          <div className="p-4 space-y-3 max-h-[48vh] overflow-y-auto bg-slate-50">
            {loading && <div className="text-sm text-slate-500">Mesajlar yukleniyor...</div>}

            {!loading && messages.length === 0 && (
              <div className="text-center py-10">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">Henuz destek mesaji bulunmuyor</p>
                <p className="text-xs text-slate-500 mt-1">Asagidaki alandan yeni bir talep olusturabilirsiniz.</p>
              </div>
            )}

            {messages.map((item) => {
              const isDriver = item.senderRole === 'external-driver' || item.senderRole === 'driver' || item.senderRole === 'internal-driver';
              return (
                <div key={item.id} className={`flex ${isDriver ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${isDriver ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                    <div className={`text-[11px] mb-1 ${isDriver ? 'text-blue-100' : 'text-slate-500'}`}>
                      {isDriver ? 'Siz' : item.senderName || 'Destek Ekibi'}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{item.message}</p>
                    <div className={`text-[11px] mt-2 ${isDriver ? 'text-blue-100' : 'text-slate-400'}`}>
                      {new Date(item.createdAt).toLocaleString('tr-TR')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-4 border-t border-slate-100 bg-white">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              placeholder="Sorununuzu veya talebinizi aciklayin..."
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Mumkunse sevkiyat no, tarih veya belge turu bilgisini ekleyin.</p>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || !draft.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-slate-300"
              >
                <SendHorizonal className="w-4 h-4" />
                {sending ? 'Gonderiliyor' : 'Mesaji Gonder'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <DriverBottomNav />
    </div>
  );
}
